/**
 * The event envelope. `data` is deliberately typed loosely and deliberately NOT trusted into the
 * UI: every handler reacts by invalidating a query key, so the value the user finally sees came
 * through the audience-specific REST mapper. See plans.md §6 (ANON-W-01..04).
 */
export type WsEventType =
  | 'notification.new'
  | 'notification.unread_count'
  | 'order.status_changed'
  | 'order.created'
  | 'cart.updated'
  | 'cart.recalculated'
  | 'queue.approvals'
  | 'queue.pricing'
  | 'notification.dead'
  // Support chat: a frame names the changed thing; the client refetches over REST.
  | 'chat.message'
  | 'chat.read'
  // Support calls: `id` is the call — state changed / a signal is waiting. SDP/ICE never
  // ride a frame; the peer drains GET /chat/calls/{id}/signals.
  | 'call.state'
  | 'call.signal';

export interface WsEvent<T = unknown> {
  type: WsEventType | string;
  /** The changed entity's id, when a refetch can be scoped by it. Never a payload. */
  id?: string;
  data?: T;
}

export function parseWsEvent(raw: string): WsEvent | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && typeof (parsed as WsEvent).type === 'string') {
      return parsed as WsEvent;
    }
  } catch {
    // A malformed frame is not worth crashing the tab over.
  }
  return null;
}
